import { useEffect, useMemo, useState } from 'react'
import { api } from '../data/api'

const PAGE_SIZE = 5

function fmtInr(n) {
  return `₹${Number(n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

// Statements (like Invoices) only exist for months that have actually
// closed — matches Google's own Statement, issued at month-end, not a live
// mid-month preview. period.key is "YYYY-MM"; lexicographic compare works.
function currentMonthKeyIST() {
  const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000)
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`
}

function DownloadIcon() {
  return (
    <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2v8m0 0L5 7m3 3l3-3M3 13h10" />
    </svg>
  )
}

// Unified view combining purchases, itemized AI usage (who did it, not just
// the total), and invoice downloads for closed months — mirrors Google AI
// Studio's own "Transactions" page (running activity + documents per
// period), not just a flat invoice list.
export default function TransactionsTab({ user, showToast }) {
  const [periods, setPeriods] = useState(null)
  const [downloadingId, setDownloadingId] = useState(null)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  useEffect(() => {
    api.get('/api/transactions')
      .then(setPeriods)
      .catch(() => { showToast?.('Failed to load transactions.', 'error'); setPeriods([]) })
    // Re-fetches whenever the balance changes (e.g. right after a Buy Credits
    // purchase elsewhere on the page) — otherwise this tab, once mounted,
    // never learns a new payment happened until it's unmounted/remounted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.creditsBalance])

  const filteredPeriods = useMemo(() => {
    if (!periods) return []
    const fromKey = fromDate ? fromDate.slice(0, 7) : null
    const toKey = toDate ? toDate.slice(0, 7) : null
    return periods.filter(p => (!fromKey || p.key >= fromKey) && (!toKey || p.key <= toKey))
  }, [periods, fromDate, toDate])

  function applyDateFilter(next) {
    setFromDate(next.from ?? fromDate)
    setToDate(next.to ?? toDate)
    setVisibleCount(PAGE_SIZE)
  }

  // A plain window.open(blobUrl) just opens the browser's built-in PDF
  // viewer in a new tab — a temporary <a download> is what actually forces
  // a file save to the user's Downloads folder.
  function triggerDownload(url, filename) {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  async function handleDownload(invoiceId, invoiceNumber) {
    setDownloadingId(invoiceId)
    try {
      const url = await api.downloadFile(`/api/invoices/${invoiceId}/download`)
      triggerDownload(url, `${invoiceNumber || 'Invoice'}.pdf`)
    } catch (err) {
      showToast?.(err.message || 'Failed to download invoice.', 'error')
    } finally {
      setDownloadingId(null)
    }
  }

  async function handleDownloadStatement(periodKey) {
    const id = `statement-${periodKey}`
    setDownloadingId(id)
    try {
      const url = await api.downloadFile(`/api/transactions/statement/${periodKey}/download`)
      triggerDownload(url, `Statement-${periodKey}.pdf`)
    } catch (err) {
      showToast?.(err.message || 'Failed to download statement.', 'error')
    } finally {
      setDownloadingId(null)
    }
  }

  if (!periods) return <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>Loading…</p>

  if (periods.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>No transactions yet — purchases and AI usage will show up here.</p>
      </div>
    )
  }

  const currentMonthKey = currentMonthKeyIST()
  const visiblePeriods = filteredPeriods.slice(0, visibleCount)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>From</span>
        <input type="date" value={fromDate} max={toDate || undefined}
          onChange={e => applyDateFilter({ from: e.target.value })}
          className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>To</span>
        <input type="date" value={toDate} min={fromDate || undefined}
          onChange={e => applyDateFilter({ to: e.target.value })}
          className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
        {(fromDate || toDate) && (
          <button onClick={() => applyDateFilter({ from: '', to: '' })}
            className="text-xs font-medium px-2 py-1 rounded-lg" style={{ color: 'var(--text-3)' }}>
            ✕ Clear dates
          </button>
        )}
      </div>

      {filteredPeriods.length === 0 ? (
        <p className="text-sm py-6 text-center" style={{ color: 'var(--text-3)' }}>No transactions in this date range.</p>
      ) : (
        <div className="space-y-6">
          {visiblePeriods.map(period => (
            <div key={period.key} className="card p-5">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>{period.periodLabel}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {period.key < currentMonthKey && (
                    <button onClick={() => handleDownloadStatement(period.key)} disabled={downloadingId === `statement-${period.key}`}
                      className="btn-ghost text-xs inline-flex items-center gap-1.5">
                      <DownloadIcon />
                      {downloadingId === `statement-${period.key}` ? 'Downloading…' : 'Download Statement'}
                    </button>
                  )}
                  {period.invoice && (
                    <button onClick={() => handleDownload(period.invoice._id, period.invoice.invoiceNumber)} disabled={downloadingId === period.invoice._id}
                      className="btn-ghost text-xs inline-flex items-center gap-1.5">
                      <DownloadIcon />
                      {downloadingId === period.invoice._id ? 'Downloading…' : 'Download Invoice'}
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-1.5">
                {period.payments.map(p => (
                  <div key={p._id} className="flex items-center justify-between text-xs py-1 gap-2 flex-wrap">
                    <span style={{ color: 'var(--text-2)' }}>{fmtDate(p.date)} · {p.label}</span>
                    <span className="font-semibold flex-shrink-0" style={{ color: '#34d399' }}>+{fmtInr(p.amount)}</span>
                  </div>
                ))}
                {period.usage.map(u => (
                  <div key={u._id} className="flex items-center justify-between text-xs py-1 gap-2 flex-wrap">
                    <span style={{ color: 'var(--text-2)' }}>
                      {fmtDate(u.date)} · {u.label}
                      {u.actorName && <span style={{ color: 'var(--text-3)' }}> — {u.actorName}{u.actorEmail ? ` (${u.actorEmail})` : ''}</span>}
                    </span>
                    <span className="flex-shrink-0" style={{ color: 'var(--text-3)' }}>-{fmtInr(u.total)}</span>
                  </div>
                ))}
                {period.payments.length === 0 && period.usage.length === 0 && (
                  <p className="text-xs py-1" style={{ color: 'var(--text-3)' }}>No activity.</p>
                )}
              </div>
            </div>
          ))}

          {filteredPeriods.length > visibleCount && (
            <div className="flex justify-center">
              <button onClick={() => setVisibleCount(c => c + PAGE_SIZE)} className="btn-ghost text-xs px-4 py-2">
                See more
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
